import React from 'react';
import cn from 'classnames';
import styles from './index.module.scss';

import { UiKit } from 'components';
import { useId } from 'utils/hooks';

/**
 * @description Design: https://www.figma.com/design/WMwUaibi2LRURMHSKSxcj5/SSDK-Components?node-id=3-78&p=f&m=dev
 * @param {React.InputHTMLAttributes<HTMLInputElement> & {
 *  label?: string,
 *  caption?: string,
 *  helptext?: string,
 *  isActive?: boolean,
 *  onChange?: (value: boolean, data: { event: React.ChangeEvent<HTMLInputElement>, [key: string]: any }) => void,
 *  size?: import('utils/types').Size,
 *  weight?: import('utils/types').Weight,
 *  direction?: "rtl" | "ltr"
 *  className?: string,
 *  tooltipMessage?: string
 * }} props - The properties for the toggle component.
 */
export default function Toggle({
  id = '',
  name = '',
  label = '',
  caption = '',
  helptext = '',
  weight = 'bold',
  isActive = false,
  disabled = false,
  size = 'md',
  direction = 'ltr',
  className = '',
  onChange = () => {},
  tooltipMessage = ''
}) {
  const internalId = useId('toggle');

  const onToggle = event => {
    if (disabled) return;
    onChange(!isActive, event);
  };

  return (
    <div
      data-size={size}
      data-checked={isActive}
      data-disabled={disabled}
      data-direction={direction}
      className={styles.container}
    >
      <UiKit.Label
        forceMount
        text={label}
        weight={weight}
        helptext={helptext}
        className={cn(styles.switch, className)}
      >
        <input
          type="checkbox"
          id={id || internalId}
          name={name}
          checked={isActive}
          disabled={disabled}
          onChange={onToggle}
          className={styles.input}
        />
        <UiKit.Tooltip>
          <UiKit.Tooltip.Trigger className={styles.content}>
            <span className={styles.slider} />
          </UiKit.Tooltip.Trigger>
          <UiKit.Tooltip.Content>{tooltipMessage}</UiKit.Tooltip.Content>
        </UiKit.Tooltip>
      </UiKit.Label>
      <UiKit.Caption className={styles.caption}>{caption}</UiKit.Caption>
    </div>
  );
}
